import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, Modal, RefreshControl, ActivityIndicator } from 'react-native';
import { useFocusEffect, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import ContentContainer from '../../../src/components/ContentContainer';
import { useToast } from '../../../src/components/Toast';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase, formatPhone } from '../../../src/utils/format';

interface FleetApplication {
  id: string;
  companyName: string;
  billingEmail: string;
  billingAddress: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingZip: string | null;
  managerName: string;
  managerEmail: string;
  managerPhone: string | null;
  estMonthlyVolume: number | null;
  vehicleCount: number | null;
  notes: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  createdFleetAccountId: string | null;
}

interface PricingTier { id: string; tierName: string }

export default function OwnerFleetApplicationsScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();
  const { isOffline } = useNetwork();

  const [items, setItems] = useState<FleetApplication[]>([]);
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');

  const [approving, setApproving] = useState<FleetApplication | null>(null);
  const [approveTierId, setApproveTierId] = useState('');
  const [approveDays, setApproveDays] = useState('15');
  const [approveAutoGen, setApproveAutoGen] = useState<'off' | 'generate_notify' | 'generate_send'>('off');
  const [approveFrequency, setApproveFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('monthly');
  const [approveDayOfWeek, setApproveDayOfWeek] = useState('1');
  const [approveSaving, setApproveSaving] = useState(false);

  const [rejecting, setRejecting] = useState<FleetApplication | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  const fetchData = useCallback(async () => {
    let tiersFailed = false;
    try {
      setLoading(true);
      const params = filter === 'pending' ? '?status=pending' : '';
      const [list, t] = await Promise.all([
        apiGet<FleetApplication[]>(`/api/fleet/applications${params}`),
        apiGet<PricingTier[]>('/api/fleet/pricing-tiers').catch(() => { tiersFailed = true; return [] as PricingTier[]; }),
      ]);
      setItems(list);
      setTiers(t);
      if (tiersFailed && !isOffline) {
        toast.show({ type: 'warning', title: 'Pricing tiers didn’t load' });
      }
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Error', message: err.message });
      }
    } finally {
      setLoading(false);
    }
  }, [filter, isOffline, toast]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  const openApprove = (item: FleetApplication) => {
    setApproving(item);
    setApproveTierId(tiers[0]?.id ?? '');
    setApproveDays('15');
    setApproveAutoGen('off');
    setApproveFrequency('monthly');
    setApproveDayOfWeek('1');
  };

  const doApprove = async () => {
    if (!approving) return;
    const days = parseInt(approveDays || '15', 10);
    if (!Number.isFinite(days) || days < 0 || days > 365) {
      toast.show({ type: 'error', title: 'Error', message: 'Net days must be between 0 and 365' });
      return;
    }
    setApproveSaving(true);
    try {
      await apiPost(`/api/fleet/applications/${approving.id}/approve`, {
        pricingTierId: approveTierId || undefined,
        paymentTermsDays: days,
        invoiceAutoGeneration: approveAutoGen,
        autoGenFrequency: approveAutoGen === 'off' ? null : approveFrequency,
        autoGenDayOfWeek:
          (approveAutoGen !== 'off' && (approveFrequency === 'weekly' || approveFrequency === 'biweekly'))
            ? parseInt(approveDayOfWeek, 10)
            : null,
      });
      toast.show({ type: 'success', title: 'Approved', message: `${approving.companyName} added to your fleet customers.` });
      setApproving(null);
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setApproveSaving(false);
    }
  };

  const doReject = async () => {
    if (!rejecting) return;
    setRejectSaving(true);
    try {
      await apiPost(`/api/fleet/applications/${rejecting.id}/reject`, {
        reason: rejectReason.trim() || undefined,
      });
      toast.show({ type: 'success', title: 'Rejected', message: `Decline notice sent to ${rejecting.managerEmail}.` });
      setRejecting(null);
      setRejectReason('');
      fetchData();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setRejectSaving(false);
    }
  };

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const statusColor = (s: string) => s === 'approved' ? colors.success : s === 'rejected' ? colors.danger : colors.warning;

  if (loading && items.length === 0) {
    return <View style={styles.loadingContainer}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  return (
    <ScrollView style={styles.container} refreshControl={<RefreshControl refreshing={loading} onRefresh={fetchData} />}>
      <ContentContainer>
        <View style={styles.filterRow}>
          <Button title="Pending" onPress={() => setFilter('pending')} variant={filter === 'pending' ? 'primary' : 'outline'} size="sm" style={{ flex: 1 }} />
          <Button title="All" onPress={() => setFilter('all')} variant={filter === 'all' ? 'primary' : 'outline'} size="sm" style={{ flex: 1 }} />
        </View>

        {items.length === 0 ? (
          <Card style={styles.empty}>
            <Ionicons name="mail-open-outline" size={48} color={colors.textLight} />
            <Text style={styles.emptyTitle}>No {filter === 'pending' ? 'pending' : ''} applications</Text>
            <Text style={styles.emptyText}>Share your fleet signup QR to start receiving applications from new fleet customers.</Text>
          </Card>
        ) : (
          items.map(item => (
            <Card key={item.id} style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>{item.companyName}</Text>
                <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '20' }]}>
                  <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status}</Text>
                </View>
              </View>
              <Text style={styles.cardLine}><Text style={styles.label}>Manager:</Text> {titleCase(item.managerName)} · {item.managerEmail}{item.managerPhone ? ` · ${formatPhone(item.managerPhone)}` : ''}</Text>
              <Text style={styles.cardLine}><Text style={styles.label}>Billing:</Text> {item.billingEmail}</Text>
              {(item.billingAddress || item.billingCity) ? (
                <Text style={styles.cardLine}><Text style={styles.label}>Address:</Text> {[item.billingAddress, item.billingCity, item.billingState, item.billingZip].filter(Boolean).join(', ')}</Text>
              ) : null}
              {(item.estMonthlyVolume || item.vehicleCount) ? (
                <Text style={styles.cardLine}>
                  <Text style={styles.label}>Volume:</Text> {item.estMonthlyVolume ?? '—'} washes/mo · <Text style={styles.label}>Vehicles:</Text> {item.vehicleCount ?? '—'}
                </Text>
              ) : null}
              {item.notes ? <Text style={styles.cardNote}>{item.notes}</Text> : null}
              <Text style={styles.cardSub}>Submitted {fmtDate(item.createdAt)}{item.reviewedAt ? ` · Reviewed ${fmtDate(item.reviewedAt)}` : ''}</Text>
              {item.status === 'rejected' && item.rejectionReason ? <Text style={styles.cardSub}>Reason: {item.rejectionReason}</Text> : null}
              {item.status === 'approved' && item.createdFleetAccountId ? (
                <Button title="Open Fleet Account" onPress={() => router.push(`/owner/fleet/${item.createdFleetAccountId}` as never)} variant="outline" size="sm" style={{ marginTop: spacing.sm }} />
              ) : null}
              {item.status === 'pending' ? (
                <View style={styles.actions}>
                  <Button title="Approve" onPress={() => openApprove(item)} variant="primary" size="sm" style={{ flex: 1 }} />
                  <Button title="Reject" onPress={() => setRejecting(item)} variant="outline" size="sm" style={{ flex: 1 }} />
                </View>
              ) : null}
            </Card>
          ))
        )}

        <View style={{ height: spacing.xxl }} />
      </ContentContainer>

      <Modal visible={!!approving} transparent animationType="slide" onRequestClose={() => setApproving(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <Text style={styles.modalTitle}>Approve {approving?.companyName}</Text>
            <Text style={styles.modalSub}>Set their pricing tier and invoice payment terms. This creates the fleet account and invites {approving?.managerEmail} to RinsePay if they don&apos;t already have an account.</Text>
            <Picker
              label="Pricing Tier"
              value={approveTierId}
              onValueChange={setApproveTierId}
              options={[{ label: 'None', value: '' }, ...tiers.map(t => ({ label: t.tierName, value: t.id }))]}
            />
            <Text style={styles.fieldLabel}>Net days until due</Text>
            <TextInput
              style={styles.input}
              value={approveDays}
              onChangeText={setApproveDays}
              keyboardType="numeric"
              placeholder="15"
              placeholderTextColor={colors.textLight}
              maxLength={3}
            />
            <Picker
              label="Auto Invoicing"
              value={approveAutoGen}
              onValueChange={(v) => setApproveAutoGen(v as 'off' | 'generate_notify' | 'generate_send')}
              options={[
                { label: 'Off — bill manually', value: 'off' },
                { label: 'Generate & Notify Me', value: 'generate_notify' },
                { label: 'Generate & Auto-Send', value: 'generate_send' },
              ]}
            />
            {approveAutoGen !== 'off' ? (
              <>
                <Picker
                  label="Frequency"
                  value={approveFrequency}
                  onValueChange={(v) => setApproveFrequency(v as 'weekly' | 'biweekly' | 'monthly')}
                  options={[
                    { label: 'Weekly (prior 7 days)', value: 'weekly' },
                    { label: 'Biweekly (prior 14 days)', value: 'biweekly' },
                    { label: 'Monthly (1st of month, prior full month)', value: 'monthly' },
                  ]}
                />
                {(approveFrequency === 'weekly' || approveFrequency === 'biweekly') ? (
                  <Picker
                    label="Run Day"
                    value={approveDayOfWeek}
                    onValueChange={setApproveDayOfWeek}
                    options={[
                      { label: 'Sunday', value: '0' },
                      { label: 'Monday', value: '1' },
                      { label: 'Tuesday', value: '2' },
                      { label: 'Wednesday', value: '3' },
                      { label: 'Thursday', value: '4' },
                      { label: 'Friday', value: '5' },
                      { label: 'Saturday', value: '6' },
                    ]}
                  />
                ) : null}
              </>
            ) : null}
            <View style={styles.modalActions}>
              <Button title="Cancel" onPress={() => setApproving(null)} variant="outline" style={{ flex: 1 }} />
              <Button title={approveSaving ? 'Approving...' : 'Approve'} onPress={doApprove} loading={approveSaving} style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={!!rejecting} transparent animationType="slide" onRequestClose={() => setRejecting(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <Text style={styles.modalTitle}>Reject {rejecting?.companyName}</Text>
            <Text style={styles.modalSub}>Optional reason — included in the polite decline email to {rejecting?.managerEmail}.</Text>
            <TextInput
              style={[styles.input, { height: 96, textAlignVertical: 'top' }]}
              value={rejectReason}
              onChangeText={setRejectReason}
              placeholder="e.g. We're not taking new fleet customers right now."
              placeholderTextColor={colors.textLight}
              multiline
              maxLength={1000}
            />
            <View style={styles.modalActions}>
              <Button title="Cancel" onPress={() => { setRejecting(null); setRejectReason(''); }} variant="outline" style={{ flex: 1 }} />
              <Button title={rejectSaving ? 'Rejecting...' : 'Reject'} onPress={doReject} loading={rejectSaving} variant="danger" style={{ flex: 1 }} />
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  filterRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.lg },

  empty: { alignItems: 'center', paddingVertical: spacing.xxl },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs, maxWidth: 320 },

  card: { marginBottom: spacing.md },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm },
  cardTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, flex: 1, marginRight: spacing.sm },
  cardLine: { fontSize: fontSize.sm, color: c.text, marginBottom: 2 },
  cardNote: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.sm, fontStyle: 'italic' },
  cardSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: spacing.sm },
  label: { fontWeight: fontWeight.medium, color: c.textSecondary },
  badge: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.sm },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.medium, textTransform: 'capitalize' },
  actions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.md },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modal: { backgroundColor: c.surface, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, gap: spacing.sm },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  modalSub: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.sm, marginBottom: spacing.xs },
  input: { borderWidth: 1, borderColor: c.border, borderRadius: borderRadius.md, padding: spacing.md, fontSize: fontSize.md, color: c.text, backgroundColor: c.background },
  modalActions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
